import axios from "axios";
import { BACK_ENDPOINT } from "@/store/conf/api";
import delay from "delay";
import { ScriptSlideLayout, Slide, SlideGenerationMode, SlideTemplate } from "@/store/slides2Video/slides2VideoModels";
import {
  DOCUMENT_TO_SLIDE,
  INIT_SLIDES_IF_NEEDED,
  OPEN_PANEL_OPTIONS,
  RESET_SLIDES_2_VIDEO,
  SELECT_CUSTOM_INSTRUCTION,
  SELECT_GENERATION_MODE,
  SELECT_SLIDE,
  SLIDE_TO_LLM,
  SLIDES_GENERATION_AUTO_MODE,
  SLIDES_TO_LLM_IN_PROGRESS,
  SLIDES_TO_LLM_PROGRESSION,
  SLIDES_TO_LLM_RESPONSE,
  UPDATE_SLIDES
} from "@/store/slides2Video/slides2VideoAction";
import { Vue } from "vue-property-decorator";
import { clone } from "@/utils/cloneUtil";
import store from "@/store";
import { OPEN_AVATAR_EDITING_CONTENT } from "@/store/AIAvatar/AIAVatarAction";
import { Avatar } from "@/store/AIAvatar/aiavatarGenerationModel";

export const SLIDE_API = BACK_ENDPOINT + '/slides';


export default {
  state: {
    proceedSlideInProgress:false,
    slideToLLMProgression:"",
    panelOptionOpen:false,
    slides:[],
    currentSlideIndex:-1,
    generationMode:SlideGenerationMode.ENHANCED,
    customInstruction:"",
    slideGenerationAutoMode:false,
  },
  mutations: {
    // Save is a slide To LLM is in progress
    [SLIDES_TO_LLM_IN_PROGRESS]: (state: any, value: boolean) => {
      state.proceedSlideInProgress=value;
    },
    [SLIDES_GENERATION_AUTO_MODE]: (state: any, value: boolean) => {
      state.slideGenerationAutoMode=value;
    },
    [SLIDES_TO_LLM_PROGRESSION]: (state: any, value: string) => {
      state.slideToLLMProgression=value;
    },
    // Update a slide
    [UPDATE_SLIDES]: (state: any, value: Slide[]) => {
      Vue.set(state,'slides',value);
    },
    // Select a slide
    [SELECT_SLIDE]:(state:any,index:number)=>{
      state.currentSlideIndex=index;
    },
    [SELECT_GENERATION_MODE]:(state:any,generationMode:SlideGenerationMode)=>{
      state.generationMode=generationMode;
    },
    [SELECT_CUSTOM_INSTRUCTION]:(state:any,customInstruction:string)=>{
      state.customInstruction=customInstruction;
    },
    [OPEN_PANEL_OPTIONS]:(state:any,value:boolean)=>{
      state.panelOptionOpen=value;
    }
  },
  getters: {
    isProceedSlideInProgress: (state: any): boolean => {
      return state.proceedSlideInProgress;
    },
    isSlideAutoMode:(state:any): boolean => {
      return state.slideGenerationAutoMode
    },
    getSlideToLLMProgression: (state: any): string => {
      return state.slideToLLMProgression;
    },
    slides:(state: any):Slide[] => {
      return state.slides
    },
    getCurrentSlideIndex(state: any):number {
      return state.currentSlideIndex;
    },
    getGenerationMode(state: any):SlideGenerationMode {
      return state.generationMode;
    },
    getCustomInstruction(state: any):string {
      return state.customInstruction;
    },
    isPanelOptionOpen(state: any):boolean {
      return state.panelOptionOpen;
    },
  },
  actions: {
    // Make sure there is a slide to work on, WITHOUT throwing away the user's work.
    // Entering the AI avatar mode must not destroy a deck that is already there:
    // only an explicit action (RESET_SLIDES_2_VIDEO) is allowed to do that.
    [INIT_SLIDES_IF_NEEDED]({commit, dispatch, state}: { commit: any, dispatch: any, state: any }) {
      if (state.slides && state.slides.length > 0) {
        // the deck is kept; just make sure a slide is selected
        if (state.currentSlideIndex < 0 || state.currentSlideIndex >= state.slides.length) {
          commit(SELECT_SLIDE, 0);
        }
        return;
      }
      dispatch(RESET_SLIDES_2_VIDEO);
    },
    // Reset Slides Module
    [RESET_SLIDES_2_VIDEO]({commit, dispatch}: { commit: any, dispatch: any }) {
      const slide = new Slide();
      slide.layout = store.getters.getAvatarScriptSlideLayout || ScriptSlideLayout.VOICE_ONLY;
      slide.avatar = store.getters.getAvatarSelected || null;
      slide.voice = null;
      slide.lang = null;
      slide.avatarBackground = store.getters.getAvatarBabckgroundSelected || null;
      slide.slideTitle = "";
      slide.slideDescription = "";
      slide.slideTemplate = SlideTemplate.AVATAR_LEFT;
      slide.slideTheme = store.getters.getFavSlideTheme || store.getters.getThemeColor;
      slide.slideBackgroundColor = store.getters.getFavSlideBackgroundColor || "";
      slide.originalDocumentB64 = "";
      slide.promptIllustrationGeneration = "";
      slide.order = 1;
      commit(UPDATE_SLIDES, [slide]);
      commit(SELECT_SLIDE, 0);
    },
    // Select a Slide
    [SELECT_SLIDE]({commit, dispatch}: { commit: any, dispatch: any },index:number) {
      commit(SELECT_SLIDE,index)
    },
    [SLIDES_GENERATION_AUTO_MODE]({commit, dispatch}: { commit: any, dispatch: any },value:boolean) {
      commit(SLIDES_GENERATION_AUTO_MODE,value)
    },
    [SELECT_GENERATION_MODE]({commit, dispatch}: { commit: any, dispatch: any },generationMode:SlideGenerationMode) {
      commit(SELECT_GENERATION_MODE,generationMode)
    },
    [SELECT_CUSTOM_INSTRUCTION]({commit, dispatch}: { commit: any, dispatch: any },customInstruction:string) {
      commit(SELECT_CUSTOM_INSTRUCTION,customInstruction)
    },
    [OPEN_PANEL_OPTIONS]({commit, dispatch}: { commit: any, dispatch: any },value:boolean) {
      commit(OPEN_PANEL_OPTIONS,value)
    },
    async [DOCUMENT_TO_SLIDE]({commit, dispatch}: { commit: any, dispatch: any }, file:File) {
      const formData = new FormData();
      formData.append('slides', file);

      await axios({
        url: SLIDE_API + '/documents2Slides',
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Content-Disposition': 'form-data; name=slides',
        },
      }).then(async (resp) => {
        const jobID=resp.data;
        // now wait the slide proceed by llm
        const DELAY=2000;
        let llmResponse:string|Slide[]="";
        while(!llmResponse){
          await delay(DELAY);
          llmResponse=await dispatch(SLIDES_TO_LLM_RESPONSE,jobID)
        }


        // Remap document name and page
        let order=1;

        for(const slide of llmResponse as Slide[]){
          slide.documentName=file.name;
          slide.order=order;
          if(!slide.layout){
            slide.layout=ScriptSlideLayout.VOICE_ONLY;
          }
          if(!slide.fileID){
            slide.documentType="PDF";
            slide.fileID=""
            slide.videoTimestampEnd=-1;
            slide.videoTimestampStart=-1;
          }
          if(!slide.chunkVideoUrl){
            slide.chunkVideoUrl="";
          }
          slide.slideImageB64="";
          order++;
        }

        commit(UPDATE_SLIDES,llmResponse)
        commit(SELECT_SLIDE,0)
        return llmResponse;
      }).catch((err) => {
        if (err.data) {
          err = err.data;
        }
        throw err;
      });
    },
    // Update a slide
    [UPDATE_SLIDES]({commit, dispatch}: { commit: any, dispatch: any },slides:Slide[]) {
      commit(UPDATE_SLIDES,slides)
    },
    [SLIDES_TO_LLM_IN_PROGRESS]({commit, dispatch}: { commit: any, dispatch: any },value:false) {
      commit(SLIDES_TO_LLM_IN_PROGRESS,value)
    },
    // Get the slide to LLM response
    async [SLIDES_TO_LLM_RESPONSE]({commit, dispatch}: { commit: any, dispatch: any }, jobID:string) {
      return await axios({
        url: SLIDE_API + '/slides2LLM/'+jobID,
        method: 'GET',
      }).then((resp) => {
        const llmData=resp.data;
        // now wait the slide proceed by llm
        if(llmData==="ERROR"){
          throw new Error("Impossible to proceed this documents")
        }
        if (llmData) {
            // decode base64 withtout issue utf-8 issue
            const decodedString = new TextDecoder('utf-8').decode(Uint8Array.from(atob(llmData), c => c.charCodeAt(0)));
            return JSON.parse(decodedString);
        }
        return
      }).catch((err) => {
        if (err.data) {
          err = err.data;
        }
        throw err;
      });
    },
    // Launch the slides to LLM process
    async [SLIDE_TO_LLM]({commit, dispatch}: { commit: any, dispatch: any },
                         {slides,hiddenB64Instruction,lang,generationMode,allowedSlideTemplates,annotationsEnabled}:
                             {slides:Slide[],hiddenB64Instruction:string|null,
                               lang:string|null,generationMode:SlideGenerationMode|null,
                               allowedSlideTemplates?:string[],annotationsEnabled?:boolean}) {
      commit(SLIDES_TO_LLM_IN_PROGRESS,true)
      store.dispatch(OPEN_AVATAR_EDITING_CONTENT, true)
      const slidesClean=clone(slides) as Slide[]
      for(const slide of slidesClean){
        slide.originalDocumentB64="";
      }

      await axios({
        url: SLIDE_API + '/slides2LLM',
        method: 'POST',
        data: {slides:slidesClean,
          customInstructionB64:hiddenB64Instruction,
          generationMode:generationMode,
          lang:lang,
          allowedSlideTemplates:allowedSlideTemplates ?? [],
          annotationsEnabled:annotationsEnabled ?? true},
      }).then(async (resp) => {
        const jobID=resp.data;
        // now wait the slide proceed by llm
        const DELAY=2000;
        let llmResponse:Slide[]|undefined=undefined;
        commit(SLIDES_TO_LLM_PROGRESSION, "")
        while(!llmResponse){
          await delay(DELAY);
          llmResponse=await dispatch(SLIDES_TO_LLM_RESPONSE,jobID) as Slide[]
          // check if progress or a real response
          if(llmResponse){
            const progress = parseInt(llmResponse+"", 10);
            if (!isNaN(progress)) {
              commit(SLIDES_TO_LLM_PROGRESSION, progress+"")
              // commit progress
              llmResponse=undefined
            }
          }
        }
        commit(SLIDES_TO_LLM_PROGRESSION, "")

        // now reapply the image and theme of slides
        for(const slide of llmResponse){
          // A video section has no page: documentPage stays at its default (0) and
          // the LLM does not echo it back (it answers 1 whatever we send). Matching
          // on it would lose the original slide, and with it documentType — which is
          // what drives the video branch of AIAvatarSource, hence the chunk creation.
          // So video slides are re-associated on the document name alone.
          const originalSlide=slides.find((s)=>{
            if(s.documentName.normalize()!==slide.documentName.normalize()){
              return false;
            }
            return s.documentType==="VIDEO" || s.documentPage===slide.documentPage;
          });
          slide.originalDocumentB64=originalSlide?.originalDocumentB64||"";
          slide.documentType=originalSlide?.documentType||"";
          slide.slideTheme=slide.slideTheme||store.getters.getFavSlideTheme||store.getters.getThemeColor
          slide.slideBackgroundColor=slide.slideBackgroundColor||store.getters.getFavSlideBackgroundColor||""
          slide.slideImageB64=""
        }
        commit(UPDATE_SLIDES,llmResponse)
        commit(SELECT_SLIDE,0)
        commit(SLIDES_TO_LLM_IN_PROGRESS,false)
        return llmResponse;
      }).catch((err) => {
        if (err.data) {
          err = err.data;
        }
        commit(SLIDES_TO_LLM_IN_PROGRESS,false)
        throw err;
      });
    }
  },
};
